import { FC, useCallback, useEffect, useState } from 'react';

import { useAuditLogsLazyQuery } from '../../../../generated/graphql';
import { ChevronDownIcon, PencilIcon } from '@heroicons/react/outline';

import Avatar from '../../../components/Avatar';
import ModalDialog from '../../../components/ModalDialog';
import Spinner from '../../../svgs/Spinner';
import { customToast } from '../../../components/ToastAlert/customToast';
import { getFullDateTimeStamp, getRelativeTimestamp } from '../../../lib/time';

const PROGRAM_FOLLOW_UP_ENTITY_TYPE =
  'app.api.models.program_follow_up.ProgramFollowUp';
const PAGE_SIZE = 20;

type MailActivityModalProps = {
  isOpen: boolean;
  setClosed: () => void;
  programFollowUpId: string;
};

const getActionLabel = (activityType: string): string => {
  switch (activityType) {
    case 'edited':
      return 'Edited';
    default:
      return activityType;
  }
};

const CHANGED_FIELD_LABELS: Record<string, string> = {
  subject: 'Subject line',
  provider_note: 'Message details',
};

const getActionIcon = (activityType: string) => {
  const iconClass = 'h-3.5 w-3.5 shrink-0';
  switch (activityType) {
    case 'edited':
      return <PencilIcon className={iconClass} />;
    default:
      return null;
  }
};

type Activity = {
  id: string;
  activityType: string;
  performedByProviderUser?: {
    id: string;
    name?: string | null;
    profileImageMedia?: { url: string } | null;
  } | null;
  performedAt: string;
  extraMetadata?: unknown;
};

const MailActivityModal: FC<MailActivityModalProps> = ({
  isOpen,
  setClosed,
  programFollowUpId,
}) => {
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allActivities, setAllActivities] = useState<Activity[]>([]);

  const [fetchAuditLogs, { loading }] = useAuditLogsLazyQuery();

  const loadPage = useCallback(
    async (pageOffset: number, append: boolean) => {
      const result = await fetchAuditLogs({
        variables: {
          entityType: PROGRAM_FOLLOW_UP_ENTITY_TYPE,
          entityId: programFollowUpId,
          limit: PAGE_SIZE,
          offset: pageOffset,
        },
      });

      if (result.error) {
        customToast.error('Unable to load activity history.');
        return 0;
      }

      const activities = (result.data?.auditLogs ?? []) as Activity[];
      setAllActivities((prev) =>
        append ? [...prev, ...activities] : activities,
      );
      setOffset(pageOffset + activities.length);
      setHasMore(activities.length >= PAGE_SIZE);
      return activities.length;
    },
    [fetchAuditLogs, programFollowUpId],
  );

  useEffect(() => {
    if (isOpen && programFollowUpId) {
      setOffset(0);
      setAllActivities([]);
      setHasMore(true);
      loadPage(0, false);
    }
  }, [isOpen, programFollowUpId, loadPage]);

  const handleLoadMore = useCallback(() => {
    loadPage(offset, true);
  }, [loadPage, offset]);

  return (
    <ModalDialog
      title="Latest activity"
      isOpen={isOpen}
      setClosed={setClosed}
      fetching={false}
      width="medium"
      overflow="auto"
      hideFooter
      bodyClassName="min-h-[200px]"
    >
      <div className="flex flex-col">
        {loading && allActivities.length === 0 ? (
          <div className="flex justify-center py-12">
            <Spinner className="h-8 w-8" />
          </div>
        ) : (
          <>
            {allActivities.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-neutral-75">
                <table className="w-full min-w-[400px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-neutral-75 bg-neutral-25">
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Team member
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Action
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Details
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allActivities.map((activity) => {
                      const name =
                        activity.performedByProviderUser?.name ?? 'Someone';
                      const imageUrl =
                        activity.performedByProviderUser?.profileImageMedia
                          ?.url;
                      const actionLabel = getActionLabel(activity.activityType);
                      const extraMetadata = activity.extraMetadata as
                        | { changed_fields?: string[] }
                        | null
                        | undefined;
                      const changedFields =
                        activity.activityType === 'edited' &&
                        extraMetadata?.changed_fields?.length
                          ? extraMetadata.changed_fields
                              .map((f) => CHANGED_FIELD_LABELS[f] ?? f)
                              .join(', ')
                          : null;
                      const details =
                        activity.activityType === 'edited' && changedFields
                          ? changedFields
                          : activity.activityType === 'edited'
                          ? 'Content updated'
                          : '—';

                      return (
                        <tr
                          key={activity.id}
                          className="border-b border-neutral-50 last:border-b-0"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Avatar
                                size="small"
                                imageUrl={imageUrl}
                                name={name}
                              />
                              <span className="text-caption text-neutral-150">
                                {name}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-50 px-2 py-0.5 text-small-label font-medium text-neutral-150">
                              {getActionIcon(activity.activityType)}
                              {actionLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-small-caption text-neutral-125">
                            {details}
                          </td>
                          <td className="px-4 py-3 text-small-caption text-neutral-125">
                            <span
                              title={
                                activity.performedAt
                                  ? getFullDateTimeStamp(activity.performedAt)
                                  : ''
                              }
                            >
                              {activity.performedAt
                                ? getRelativeTimestamp(activity.performedAt)
                                : '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {allActivities.length > 0 && hasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="flex items-center gap-1 text-caption font-medium text-secondary-100 hover:text-secondary-150 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Spinner className="h-4 w-4" />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load more
                      <ChevronDownIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            )}
            {!loading && allActivities.length === 0 && (
              <div className="py-12 text-center text-caption text-neutral-125">
                No activity yet.
              </div>
            )}
          </>
        )}
      </div>
    </ModalDialog>
  );
};

export default MailActivityModal;
